import { Formik, FieldArray } from 'formik';
import { useRef, useMemo, Fragment } from 'react';

import { useHeader } from 'context';
import { ButtonColors } from 'types';
import { useAppSelector } from 'store';
import { formatPrice } from 'utils/helpers';
import { useListQuery } from 'store/api/users';
import { EventFormValues } from 'store/api/event';
import { isMobile, useAlert, useNavigate } from 'hooks';
import { useExpensesQuery } from 'store/api/accounting';
import { formatDate, formatCalendarDate } from 'utils/date';
import { useListQuery as useThemesQuery } from 'store/api/themes';
import { useListQuery as useProductsQuery } from 'store/api/product';
import { isAuthCompanyUser, selectAuthUserType } from 'store/slices/auth';
import { useListQuery as usePaymentMethodsQuery } from 'store/api/payment-methods';
import {
  handleFormSubmitWithFiles,
  makeSelectOptionsFromEntities,
} from 'utils/helpers';
import {
  Row,
  Col,
  Flex,
  Icon,
  List,
  Button,
  ListCard,
  AddButton,
  FormFooter,
  InputField,
  ListHeader,
  RatingField,
  ButtonTypes,
  SelectField,
  SwitchField,
  DeleteButton,
  EllipsisText,
  CommonStyles,
  ErrorMessage,
  CheckboxField,
  SectionHeader,
  DatePickerField,
  StatesSelectField,
  FileDropzoneField,
  FormPhotoFieldRow,
  EventTeamAssignSelects,
  AddressAutoCompleteInputField,
} from 'components';

import * as Styles from './styles';
import { FormProps } from './types';
import { ShowHours } from './show-hours';
import { getEventExpensePaymentRows } from './payment-expenses';
import {
  listShowDates,
  schedulePatch,
  ScheduleFormRow,
  buildScheduleRows,
} from './show-hours/helpers';
import {
  validationSchema,
  getInitialValues,
  eventTypeOptions,
  paymentTypeOptions,
  transactionOptions,
  eventLocationTypeOptions,
} from './config';

export const Form = ({ data, onSubmit }: FormProps) => {
  const mobile = isMobile();
  const navigate = useNavigate();
  const authUserType = useAppSelector(selectAuthUserType);
  const isCompanyOwner = useAppSelector(isAuthCompanyUser);
  const { handleError } = useAlert();

  const { data: themes } = useThemesQuery();
  const { data: products } = useProductsQuery();
  const { data: paymentMethods } = usePaymentMethodsQuery({ status: 'active' });
  const { data: accountingExpenses } = useExpensesQuery(
    { eventIds: data?.id ? JSON.stringify([data.id]) : undefined },
    { skip: !data?.id }
  );
  const { data: users } = useListQuery({
    limit: 10000,
    hideTeamMemberRoles: true,
  });

  const isEdit = Boolean(data);
  const initialValues = useMemo(() => getInitialValues(data), [data]);
  // Hours typed for a date the picker dropped come back if it returns.
  const scheduleCache = useRef(new Map<string, ScheduleFormRow>());

  // The event request the last save started (the edit / create mutation).
  const requestRef = useRef<unknown>(undefined);
  const submitWithFiles = handleFormSubmitWithFiles<
    EventFormValues,
    typeof data
  >({
    data,
    handleError,
    files: [
      {
        formFieldName: 'floorPlan',
        requestFieldName: 'floorPlanFileUrl',
      },
      {
        formFieldName: 'photo',
        requestFieldName: 'photoUrl',
      },
    ],
    // Show hours go out as Events.schedule (CONTRACT §1) only when a row
    // was edited, pasted or copied; otherwise the key is left out and the
    // saved hours stay exactly as they are. The old weeklySchedule is
    // never written from this form.
    onSubmit: ({
      schedule,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      weeklySchedule,
      scheduleTouched,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      scheduleUnclear,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      scheduleRefused,
      ...values
    }) => {
      requestRef.current = onSubmit({
        ...values,
        endDate: formatDate(values.endDate),
        startDate: formatDate(values.startDate),
        ...schedulePatch({
          touched: scheduleTouched,
          rows: schedule as ScheduleFormRow[],
          initialRows: initialValues.schedule as ScheduleFormRow[],
        }),
      });
    },
  });

  useHeader({
    showBackButton: true,
    pageName: `${isEdit ? 'Edit' : 'New'} event`,
  });

  const themeSelectOptions = makeSelectOptionsFromEntities(themes);
  const productSelectOptions = makeSelectOptionsFromEntities(products);
  const userSelectOptions = makeSelectOptionsFromEntities(users?.rows);
  const paymentMethodOptions = makeSelectOptionsFromEntities(paymentMethods);
  const expensePaymentRows = getEventExpensePaymentRows(
    accountingExpenses,
    data?.id
  );

  return (
    <Formik<EventFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      // isSubmitting stays true until the request has answered (success or
      // failure), so the Show hours save guard lets one save through at a
      // time (6th pass: a double click created two events).
      onSubmit={async (formValues, helpers) => {
        requestRef.current = undefined;
        await submitWithFiles(formValues, helpers);
        await requestRef.current;
      }}
    >
      {({ dirty, errors, values, touched, isSubmitting, setFieldValue }) => {
        const datesError =
          (touched?.dates && errors.startDate) ||
          (touched?.dates && errors.endDate);
        const setZipcodeAndTax = (zip: string, taxRate: string) => {
          setFieldValue('zip', zip);
          setFieldValue('tax', +taxRate);
        };

        return (
          <CommonStyles.Form>
            <CommonStyles.FormInner>
              <FormPhotoFieldRow />

              <SectionHeader withBorder={false} title="Event information" />

              <Row>
                <Col col={3} colTablet={4}>
                  <InputField name="name" label="Name" placeholder="Name" />
                </Col>

                <Col col={3} colTablet={4}>
                  <DatePickerField
                    selectsRange
                    name="dates"
                    label="Select Dates"
                    placeholderText="Select Dates"
                    onChange={(dates) => {
                      setFieldValue('startDate', dates?.[0]);
                      setFieldValue('endDate', dates?.[1]);
                      if (dates?.[0] && dates?.[1]) {
                        const cache = scheduleCache.current;
                        (values.schedule as ScheduleFormRow[]).forEach((row) =>
                          cache.set(row.date, row)
                        );
                        setFieldValue(
                          'schedule',
                          buildScheduleRows({
                            saved: data?.schedule,
                            weekly: data?.weeklySchedule,
                            current: Array.from(cache.values()),
                            dates: listShowDates(dates[0], dates[1]),
                          })
                        );
                      }
                    }}
                  />
                  {datesError && <ErrorMessage>{datesError}</ErrorMessage>}
                </Col>
              </Row>

              <Row>
                <Col col={3} colTablet={6}>
                  <InputField
                    name="sellingDays"
                    label="Selling days"
                    placeholder="Selling days"
                  />
                </Col>

                <Col col={3} colTablet={6}>
                  <InputField
                    number
                    decimal
                    name="tax"
                    label="Tax, %"
                    placeholder="Tax, %"
                  />
                </Col>
              </Row>

              <Row>
                <Col col={3} colTablet={6}>
                  <AddressAutoCompleteInputField
                    zipFieldName="zip"
                    onTaxSelected={setZipcodeAndTax}
                  />
                </Col>

                <Col col={3} colTablet={6}>
                  <StatesSelectField isDisabled name="state" />
                </Col>

                <Col col={3} colTablet={6}>
                  <InputField
                    disabled
                    name="city"
                    label="City"
                    placeholder="City"
                  />
                </Col>

                <Col col={3} colTablet={6}>
                  <InputField
                    number
                    disabled
                    name="zip"
                    label="ZIP"
                    placeholder="ZIP"
                  />
                </Col>
              </Row>

              <SectionHeader withBorder={false} title="Contact information" />
              <Row>
                <Col col={3} colTablet={4}>
                  <InputField
                    name="contactName"
                    label="Contact Name"
                    placeholder="Contact Name"
                  />
                </Col>

                <Col col={3} colTablet={4}>
                  <InputField
                    type="tel"
                    label="Phone"
                    name="contactPhone"
                    placeholder="Phone"
                  />
                </Col>

                <Col col={3} colTablet={4}>
                  <InputField
                    label="Email"
                    name="contactEmail"
                    placeholder="Email"
                  />
                </Col>
              </Row>

              <SectionHeader
                withBorder={false}
                title="Event Theme & Products"
              />
              <Row>
                <Col col={3} colTablet={4}>
                  <SelectField
                    onlyValue
                    label="Theme"
                    name="themeId"
                    placeholder="Theme"
                    options={themeSelectOptions}
                  />
                </Col>

                <Col col={3} colTablet={4}>
                  <InputField name="link" label="Link" placeholder="Link" />
                </Col>

                <Col col={3} colTablet={4}>
                  <SelectField
                    onlyValue
                    label="Product"
                    name="productId"
                    placeholder="Select product"
                    options={productSelectOptions}
                  />
                </Col>
              </Row>

              <SectionHeader withBorder={false} title="Attendance" />

              <Row>
                <Col col={6} colTablet={6}>
                  <InputField
                    name="attendance"
                    label="Attendance"
                    placeholder="Add attendance"
                  />
                </Col>

                <Col col={6} colTablet={6}>
                  <InputField
                    name="vendors"
                    label="Vendors"
                    placeholder="Add vendors"
                  />
                </Col>
              </Row>

              <Row>
                <Col col={6} colTablet={6}>
                  <RatingField label="Event Rating" />
                </Col>

                <Col col={6} colTablet={6}>
                  <InputField
                    name="ratingMessage"
                    label="Event Rating Message"
                    placeholder="Add event rating message"
                  />
                </Col>
              </Row>

              <SectionHeader title="Locations" withBorder={false} />

              <FieldArray
                name="locations"
                render={({ push, remove }) => (
                  <>
                    {values.locations?.map((location, index) => (
                      <Fragment key={index}>
                        <Row>
                          <Col col={2} colTablet={4}>
                            <InputField
                              label="#"
                              placeholder="#"
                              name={`locations.${index}.number`}
                            />
                          </Col>
                          <Col col={2} colTablet={4}>
                            <InputField
                              number
                              decimal
                              label="$"
                              placeholder="$"
                              name={`locations.${index}.income`}
                            />
                          </Col>
                          <Col col={2}>
                            <Flex gap={8}>
                              <InputField
                                number
                                decimal
                                label="Length, ft"
                                placeholder="Length, ft"
                                name={`locations.${index}.length`}
                              />
                              <InputField
                                number
                                decimal
                                label="Width, ft"
                                placeholder="Width, ft"
                                name={`locations.${index}.width`}
                              />
                            </Flex>
                          </Col>

                          <Col col={2} colTablet={6}>
                            <SelectField
                              onlyValue
                              label="Location Type"
                              placeholder="Select location type"
                              options={eventLocationTypeOptions}
                              name={`locations.${index}.locationType`}
                            />
                          </Col>

                          <Col col={2} colTablet={6}>
                            <SelectField
                              onlyValue
                              label="Indoor/Outdoor"
                              options={eventTypeOptions}
                              placeholder="Select Indoor/Outdoor"
                              name={`locations.${index}.eventType`}
                            />
                          </Col>
                        </Row>

                        {(location.id || index > 0) && (
                          <Row>
                            <Col>
                              <DeleteButton
                                text="Remove location"
                                onClick={() => remove(index)}
                              />
                            </Col>
                          </Row>
                        )}
                      </Fragment>
                    ))}

                    <Row>
                      <Col>
                        <AddButton
                          text="Add location"
                          onClick={() =>
                            push({
                              number: '',
                              eventType: '',
                              locationType: '',
                            })
                          }
                        />
                      </Col>
                    </Row>
                  </>
                )}
              />

              <Row>
                <Col col={3} colTablet={4}>
                  <Flex gap={4} minWidth={0} alignItems="center">
                    <FileDropzoneField name="floorPlan" label="Floor Plan">
                      <Flex gap={4} minWidth={0} alignItems="center">
                        <Button text="Choose file" />

                        {values.floorPlan && (
                          <EllipsisText>
                            {values.floorPlan.file.name}
                          </EllipsisText>
                        )}
                      </Flex>
                    </FileDropzoneField>

                    {values.floorPlan && (
                      <Styles.DeleteFileButton
                        onClick={() => setFieldValue('floorPlan', null)}
                      />
                    )}
                  </Flex>
                </Col>

                <Col col={3} colTablet={4}>
                  <InputField
                    name="floorPlanLink"
                    label="Floor Plan Link"
                    leftIcon={<Icon.Link />}
                  />
                </Col>
                <Col col={3} colTablet={4}>
                  <InputField
                    number
                    decimal
                    name="finalPrice"
                    label="Final Price"
                    leftIcon={<Icon.CoinDollar />}
                  />
                </Col>
              </Row>

              {authUserType === 'ops' && (
                <Row>
                  <Col>
                    <InputField
                      type="textarea"
                      name="bookingTeamNotes"
                      label="Booking Team Notes"
                    />
                  </Col>
                </Row>
              )}

              <SectionHeader title="Payments" withBorder={false} />
              <FieldArray
                name="payments"
                render={({ push, remove }) => (
                  <>
                    {values.payments?.map((payment, index) => (
                      <Fragment key={index}>
                        <Row>
                          <Col col={2} colTablet={6}>
                            <SelectField
                              onlyValue
                              menuPlacement="top"
                              label="Payment Type"
                              placeholder="Payment Type"
                              options={paymentTypeOptions}
                              name={`payments.${index}.type`}
                            />
                          </Col>
                          <Col col={2} colTablet={6}>
                            <SelectField
                              onlyValue
                              menuPlacement="top"
                              label="Transaction"
                              placeholder="Transaction"
                              options={transactionOptions}
                              name={`payments.${index}.transaction`}
                            />
                          </Col>
                          <Col col={2} colTablet={6}>
                            <InputField
                              label="Notes"
                              placeholder="Notes"
                              name={`payments.${index}.notes`}
                            />
                          </Col>
                          <Col col={2} colTablet={6}>
                            <InputField
                              number
                              decimal
                              label="Amount"
                              placeholder="Amount"
                              leftIcon={<Icon.CoinDollar />}
                              name={`payments.${index}.amount`}
                            />
                          </Col>
                          <Col col={2} colTablet={6}>
                            <DatePickerField
                              label="Date"
                              placeholderText="Date"
                              name={`payments.${index}.date`}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col col={2} colTablet={6}>
                            <SelectField
                              onlyValue
                              menuPlacement="top"
                              label="Paid/Scheduled By"
                              options={userSelectOptions}
                              placeholder="Paid/Scheduled By"
                              name={`payments.${index}.paidById`}
                            />
                          </Col>
                          <Col col={3} colTablet={6}>
                            {/* UI pass 16 Oct: the method select takes the
                                column's width (every row the same, whatever
                                method is picked) and "Cashed" never wraps;
                                its box is centred on the 40 px select. */}
                            <Flex gap={16} alignItems="center">
                              <Flex column flex={1} minWidth={0}>
                                <SelectField
                                  onlyValue
                                  menuPlacement="top"
                                  label="Payment Method"
                                  options={paymentMethodOptions}
                                  name={`payments.${index}.paymentMethodId`}
                                />
                              </Flex>
                              <CheckboxField
                                label="Cashed"
                                id={`payments.${index}.cashed`}
                                name={`payments.${index}.cashed`}
                                style={{
                                  flex: 'none',
                                  marginTop: 22,
                                  whiteSpace: 'nowrap',
                                }}
                              />
                            </Flex>
                          </Col>
                        </Row>

                        <Row>
                          <Col>
                            <DeleteButton
                              text="Remove payment"
                              onClick={() => remove(index)}
                            />
                          </Col>
                        </Row>
                      </Fragment>
                    ))}

                    <AddButton
                      text="Add payment"
                      onClick={() => push({ type: '', cashed: false })}
                    />
                  </>
                )}
              />
              {!!expensePaymentRows.length && (
                <List
                  count={expensePaymentRows.length}
                  header={
                    <ListHeader
                      titles={[
                        'Payment Type',
                        'Transaction',
                        'Notes',
                        'Amount',
                        'Date',
                        'Paid/Scheduled By',
                        'Payment Method',
                        'Created By',
                      ]}
                    />
                  }
                >
                  {expensePaymentRows.map((payment) => (
                    <ListCard
                      key={`expense-payment-${payment.id}`}
                      values={[
                        payment.paymentType,
                        payment.transactionLabel,
                        payment.notes,
                        formatPrice(payment.amount),
                        // An expense's payment date is a calendar day at
                        // 00:00 UTC: show that day, not the evening before.
                        payment.date && formatCalendarDate(payment.date),
                        payment.paidBy,
                        payment.paymentMethod,
                        payment.createdBy,
                      ]}
                    />
                  ))}
                </List>
              )}

              <SectionHeader withBorder={false} title="Show hours" />
              <ShowHours
                // A new instance per event: no state carries over.
                key={data?.id ?? 'new'}
                notesVisible={authUserType === 'client'}
                datesTooLong={
                  Boolean(values.startDate && values.endDate) &&
                  !listShowDates(values.startDate, values.endDate).length
                }
              />

              {authUserType === 'client' && (
                <>
                  <SectionHeader title="Team" withBorder={false} />
                  <EventTeamAssignSelects
                    menuPlacement="top"
                    colProps={{ col: 3, colTablet: 6 }}
                  />

                  <Row>
                    <Col col={6} colTablet={6}>
                      <InputField
                        name="notes"
                        label="Notes"
                        placeholder="Notes"
                        type="textarea-autosize"
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col col={6} colTablet={6}>
                      <InputField
                        type="textarea"
                        name="notesForClient"
                        label="Notes For Client"
                      />
                    </Col>
                  </Row>
                </>
              )}

              {isCompanyOwner && (
                <>
                  <SectionHeader
                    withBorder={false}
                    title="Manual Credit Payment"
                  />
                  <Row>
                    <Col col={3}>
                      <SwitchField
                        label="Manual Credit Payment"
                        name="isManualCreditPaymentEnabled"
                      />
                    </Col>
                  </Row>
                </>
              )}
            </CommonStyles.FormInner>

            <FormFooter
              right={
                <CommonStyles.FormButtons>
                  <Button
                    text="Cancel"
                    fullWidth={mobile}
                    onClick={() => navigate(-1)}
                    colorType={ButtonColors.LightBlue}
                  />
                  <Button
                    fullWidth={mobile}
                    type={ButtonTypes.Submit}
                    text={isEdit ? 'Edit' : 'Create'}
                    disabled={!dirty || isSubmitting}
                  />
                </CommonStyles.FormButtons>
              }
            />
          </CommonStyles.Form>
        );
      }}
    </Formik>
  );
};
